import React from "react";
import { Button } from "../components/ui/button";
import { Phone, Award, MapPin, ArrowUpRight, Star, UserX } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Link } from "@inertiajs/react";

interface Physio {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  specialties: string[];
  approved_specialties?: string[];
  isCertified: boolean;
  phone: string;
  profileType: "basic" | "filled" | "certified";
  source?: "platform" | "rpps";
  distance?: number | null;
}

interface PhysioCardProps {
  physio: Physio;
  isSelected?: boolean;
  onClick: () => void;
}

const PhysioCard: React.FC<PhysioCardProps> = ({
  physio,
  isSelected = false,
  onClick
}) => {
  const { id, firstName, lastName, address, city, specialties, approved_specialties, isCertified, phone, profileType, source, distance } = physio;
  const isRppsOnly = source === "rpps";
  const distanceLabel =
    distance != null ? (distance < 1 ? "< 1 km" : `${Math.round(distance)} km`) : null;

  const handlePhoneClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent card click
    e.preventDefault(); // Prevent navigation

    // Track this click using sendBeacon
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/clicks", JSON.stringify({ physio_id: id }));
    }

    // Open phone link
    window.open(`tel:${phone}`, "_self");
  };

  // Déterminer la couleur de la bordure en fonction du type de profil
  const getBorderColor = () => {
    if (isRppsOnly) return "border-l-gray-300";
    if (profileType === "certified") return "border-l-green-500";
    if (profileType === "filled") return "border-l-blue-400";
    return "border-l-gray-200";
  };

  return (
    <Link
      href={`/physiotherapists/${id}`}
      className={`block p-5 hover:bg-gray-50 cursor-pointer transition-all duration-200 border-l-4 ${getBorderColor()} ${
        isSelected ? "bg-gray-50 shadow-sm" : ""
      } ${isRppsOnly ? "opacity-50" : ""}`}
      onClick={() => {
        // Exécuter également le onClick pour mettre à jour la carte
        onClick();
      }}
    >
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-lg text-gray-900 flex items-center gap-2">
            {firstName} {lastName}
            {isCertified && !isRppsOnly && (
              <Badge className="bg-green-500 hover:bg-green-600 rounded-full text-[10px] py-0.5 px-2">
                <Award className="h-2.5 w-2.5 mr-0.5" />
                Approuvé
              </Badge>
            )}
            {isRppsOnly && (
              <Badge className="bg-gray-200 text-gray-600 hover:bg-gray-300 rounded-full text-[10px] py-0.5 px-2">
                <UserX className="h-2.5 w-2.5 mr-0.5" />
                Non inscrit
              </Badge>
            )}
          </h3>

          <ArrowUpRight className="h-4 w-4 text-gray-400" />
        </div>

        {(address || city) && (
          <div className="flex items-center text-gray-500 text-sm">
            <MapPin className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-gray-400" />
            <span className="truncate">
              {address && city ? `${address}, ${city}` : city || address}
            </span>
            {distanceLabel && (
              <span className="ml-2 flex-shrink-0 text-xs font-medium text-green-600 whitespace-nowrap">
                {distanceLabel}
              </span>
            )}
          </div>
        )}

        {!isRppsOnly && specialties && specialties.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {specialties.slice(0, 3).map((specialty, index) => {
              const isApproved = approved_specialties?.includes(specialty);
              return (
                <Badge
                  key={index}
                  variant="secondary"
                  className={`text-xs font-normal px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    isApproved
                      ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isApproved && <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />}
                  {specialty}
                </Badge>
              );
            })}
            {specialties.length > 3 && (
              <Badge
                variant="secondary"
                className="text-xs font-normal bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-0.5 rounded-full"
              >
                +{specialties.length - 3}
              </Badge>
            )}
          </div>
        )}

        <div className="pt-1">
          {isRppsOnly ? (
            <div className="text-center text-xs text-gray-400 py-2">
              Profil non complété
            </div>
          ) : phone ? (
            <Button
              size="sm"
              onClick={handlePhoneClick}
              variant="outline"
              className="w-full justify-center rounded-full border-gray-300 hover:bg-green-50 hover:text-green-600 hover:border-green-200 transition-all"
            >
              <Phone className="h-3.5 w-3.5 mr-2" />
              Appeler
            </Button>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

export default PhysioCard;
